import { Checkbox } from 'antd';
import * as ExcelJS from 'exceljs';
import { uniqBy } from 'lodash';
import moment from 'moment';
import { adminBatchGetStudioMembers } from '../../../api';
import { messageError } from '../../../components/message';
import { ModalWrap } from '../../../components/modal-wrap';
import { getLocalTime, strToJson } from '../../../lib';
import { IPageGameManageEnvItem, IPageGameManageItem } from '../../../model';
import {
  CommonStatusStep,
  IAPIUserInfo,
  IRbacMemberItem,
  IStudioInfo,
  RoleType,
} from '../../../model/api-modules';
import { getEnvNameByValue } from '../../utils/env';
import { getUserNameBySite } from '../lib/columns';

interface IGameManageExportMeta<T = Record<string, string>> {
  label: string;
  key?: string;
  getter?: (record: T) => string | string[] | undefined | null;
}

export const exportGameManageXlsx = async (
  studioList: IStudioInfo[] = [],
  gameList: IPageGameManageItem[],
  userInfo: IAPIUserInfo | null,
  site?: string
) => {
  let selectedTypes: string[] = ['studio', 'game', 'env'];
  ModalWrap.show({
    title: '选择导出的数据维度',
    oKText: '确认',
    cancelText: '取消',
    async onOk() {
      if (!selectedTypes.length) {
        messageError('请选择导出维度');
        return false;
      }
      await startExport(studioList, gameList, selectedTypes, userInfo, site);
      return true;
    },
    children: (
      <div>
        <div style={{ marginBottom: 8 }}>选择导出的数据维度:</div>
        <div>
          <Checkbox.Group
            defaultValue={selectedTypes}
            onChange={(vals) => {
              selectedTypes = vals as string[];
            }}
          >
            <div>
              <Checkbox value="studio">工作室</Checkbox>
            </div>
            <div>
              <Checkbox value="game">游戏</Checkbox>{' '}
            </div>
            <div>
              <Checkbox value="env">环境</Checkbox>
            </div>
          </Checkbox.Group>
        </div>
      </div>
    ),
  });
};

const startExport = async (
  studioList: IStudioInfo[],
  gameList: IPageGameManageItem[],
  types: string[],
  userInfo: IAPIUserInfo | null,
  site?: string
) => {
  const isExportStudios = types.includes('studio');
  const studios = studioList;
  let studioMembers: Record<string, IRbacMemberItem[]> = {};
  if (isExportStudios) {
    studioMembers = await adminBatchGetStudioMembers(
      studios.map((item) => item?.StudioID || '')
    );
  }

  const studioMeta: IGameManageExportMeta<IStudioInfo>[] = [
    {
      label: '工作室ID',
      key: 'StudioID',
    },
    {
      label: '工作室名称',
      key: 'Name',
    },
    {
      label: '所有人',
      getter: (record) =>
        studioMembers[record.StudioID].find(
          (member) => member.roleType === RoleType.Owner
        )?.userId,
    },
    {
      label: '创建时间',
      getter: (record) =>
        getLocalTime(record.CreateTime, userInfo?.UserSettings?.Timezone),
    },
    {
      label: '成员名称',
      key: 'Name',
      getter: (record) =>
        studioMembers[record.StudioID].map(
          (member) => member.userId.split('@')?.[0]
        ),
    },
    {
      label: '成员邮箱',
      getter: (record) =>
        studioMembers[record.StudioID].map((member) => member.userId),
    },
  ];

  const gameMeta: IGameManageExportMeta<IPageGameManageItem>[] = [
    {
      label: '工作室信息',
      key: 'studioInfo',
      getter: (record) =>
        `${record.studioInfo?.Name}(${record.studioInfo?.StudioID})`,
    },
    {
      label: '游戏ID',
      key: 'GameID',
    },
    {
      label: '游戏名称',
      key: 'Name',
    },
    {
      label: '游戏代号',
      key: 'ProjectCode',
    },
    {
      label: '游戏创建人',
      getter: (record) => getUserNameBySite(record.Creator, site),
    },
    {
      label: '创建时间',
      getter: (record) =>
        getLocalTime(record.CreateTime, userInfo?.UserSettings?.Timezone),
    },
  ];

  const envMeta: IGameManageExportMeta<IPageGameManageEnvItem>[] = [
    {
      label: '工作室信息',
      key: 'studioInfo',
      getter: (record) =>
        `${record.studioInfo?.Name}(${record.studioInfo?.StudioID})`,
    },
    {
      label: '游戏信息',
      key: 'gameInfo',
      getter: (record) => `${record.gameInfo.Name}(${record.gameInfo.GameID})`,
    },
    {
      label: '游戏代号',
      key: 'ProjectCode',
      getter: (record) => `${record.gameInfo.ProjectCode}`,
    },
    {
      label: '环境ID',
      key: 'id',
    },
    {
      label: '环境名称',
      key: 'Name',
    },
    {
      label: 'EnvID',
      key: 'Guid',
    },
    {
      label: '状态',
      getter: (record) =>
        record.services.some((item) => item.Status === CommonStatusStep.Resolve)
          ? '开启'
          : '关闭',
    },
    {
      label: '环境创建人',
      getter: (record) => getUserNameBySite(record.FounderID, site),
    },
    {
      label: '创建时间',
      getter: (record) =>
        getLocalTime(record.CreateTime, userInfo?.UserSettings?.Timezone),
    },
    {
      label: '地区',
      getter: (record) => getEnvNameByValue(record.Region),
    },
    {
      label: '服务版本',
      getter: (record) =>
        strToJson<Record<string, string>>(record.GosParams || '')
          ?.GameServiceVersion,
    },
    {
      label: '已开通服务',
      getter: (record) =>
        uniqBy(record.services, 'ServiceName')
          .map((service) => service.ServiceName)
          .join('、'),
    },
    {
      label: 'Service URL',
      getter: (record) =>
        strToJson<Record<string, string>>(record.GosParams || '')?.BianQueAddr,
    },
    {
      label: 'Gate Address',
      getter: (record) =>
        strToJson<Record<string, string>>(record.GosParams || '')?.GateAddress,
    },
    {
      label: 'Realm URL',
      getter: (record) =>
        strToJson<Record<string, string>>(record.GosParams || '')?.ReamURL,
    },
  ];
  const workbook = new ExcelJS.Workbook();

  if (isExportStudios) {
    generateSheet(workbook, studioMeta, studios, '工作室列表');
  }
  if (types.includes('game')) {
    generateSheet(workbook, gameMeta, gameList, '游戏列表');
  }
  if (types.includes('env')) {
    generateSheet(
      workbook,
      envMeta,
      gameList.map((game) => game.envs).flat(),
      '环境列表'
    );
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `游戏管理_${moment().format('YYYYMMDDHHmmss')}.xlsx`; // 设置文件名
  a.click();

  URL.revokeObjectURL(url);
};

const generateSheet = (
  workbook: ExcelJS.Workbook,
  metaData: IGameManageExportMeta<any>[],
  list: any[],
  sheetName: string
) => {
  const worksheet = workbook.addWorksheet(sheetName);

  worksheet.addRow(metaData.map((item) => item.label));
  'ABCDEFGHIJKLMNOPQ'.split('').forEach((columnKey) => {
    worksheet.getColumn(columnKey).width = 26;
  });

  worksheet.getColumn('E').alignment = { wrapText: true };
  worksheet.getColumn('F').alignment = { wrapText: true };
  worksheet.getColumn('F').width = 30;
  const tableData = list.map((info) => {
    return metaData.map((meta) => {
      if (!info) {
        return '';
      }
      const cellText = meta.getter?.(info) || (info[meta.key || ''] as string);
      if (cellText instanceof Array) {
        return cellText.join('\n');
      }
      return cellText;
    });
  });

  worksheet.addRows(tableData);

  worksheet.eachRow((row) => {
    let maxLineCount = 1;

    row.eachCell((cell) => {
      cell.alignment = {
        vertical: 'top',
        horizontal: 'center',
        wrapText: true,
      };
      if (String(cell.value).includes('\n')) {
        const lines = String(cell.value).split('\n').length;
        if (lines > maxLineCount) maxLineCount = lines;
      }
    });
    // eslint-disable-next-line no-param-reassign
    row.height = maxLineCount * 15; // 基础行高 + 换行增量
  });
};
